import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  CircularProgress,
  DialogContentText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox, PersonAdd, CloudUpload, Delete as DeleteIcon, InsertDriveFile, ExpandMore, History as HistoryIcon, ZoomIn } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { createAuthUserForCompany, getUsers } from '../lib/api';
import { applyAgentChange } from '../lib/agentApi';
import RecordHistory from './RecordHistory';
import RichTextEditor from './RichTextEditor';
import { toInputDateFormat } from '../lib/dateUtils';

interface DetailDialogProps {
  open: boolean;
  onClose: () => void;
  tableName: string;
  recordId?: string | number;
  onSave: () => void;
  // 'dialog' (default): a modal popup, as before. 'page': the exact same form
  // and save logic, rendered as normal full-width page content instead of a
  // modal — used by EntityEditPage for entities with too many fields for a
  // cramped popup to work well.
  variant?: 'dialog' | 'page';
  // Seed the form (used by the AI agent's draft flow to prefill proposed values).
  initialData?: Record<string, any>;
  // 'agent' routes the save through the audited admin_apply_change RPC (source=agent).
  changeSource?: 'admin' | 'agent';
  // Called with the saved row on success (agent flow uses this to mark applied).
  onSaved?: (row: any) => void;
  onFieldChange?: (fieldName: string, value: any, formData: any) => any;
  fields: Array<{
    name: string;
    label: string;
    type?: 'text' | 'email' | 'number' | 'date' | 'textarea' | 'richtext' | 'select' | 'boolean' | 'multiselect' | 'currency' | 'targets' | 'image' | 'file';
    required?: boolean;
    disabled?: boolean;
    multiline?: boolean;
    width?: 'full' | 'half' | 'third'; // New: Control field width in grid
    // For type 'image' | 'file': the storage bucket to upload into. The public
    // URL is stored in the record column. `accept` sets the file picker filter.
    bucket?: string;
    accept?: string;
    options?: Array<{ value: any; label: string }>;
    foreignKey?: {
      table: string;
      valueField: string;
      labelField: string;
    };
    // For type 'targets': pick from multiple reference tables, stored as
    // tagged objects [{type, id}] in a single jsonb column.
    targets?: Array<{
      type: string; // tag stored on each selected item, e.g. 'city'
      label: string;
      table: string;
      valueField: string;
      labelField: string;
    }>;
  }>;
}

const DetailDialog: React.FC<DetailDialogProps> = ({
  open,
  onClose,
  tableName,
  recordId,
  onSave,
  variant = 'dialog',
  initialData,
  changeSource = 'admin',
  onSaved,
  onFieldChange,
  fields,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foreignKeyData, setForeignKeyData] = useState<Record<string, any[]>>({});
  const [authUsers, setAuthUsers] = useState<any[]>([]);
  const [creatingAuthUser, setCreatingAuthUser] = useState(false);
  const [authUserCreated, setAuthUserCreated] = useState<{ email: string; password: string } | null>(null);
  const [showAuthUserConfirm, setShowAuthUserConfirm] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const isEdit = !!recordId;

  useEffect(() => {
    if (open && recordId) {
      fetchRecord();
    } else if (open) {
      setFormData(initialData || {});
    }
    if (open) {
      fetchForeignKeyData();
      if (tableName === 'companies') {
        fetchAuthUsers();
      }
    }
    // Reset auth user creation state when dialog opens
    setAuthUserCreated(null);
    setShowAuthUserConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId]);

  const fetchAuthUsers = async () => {
    try {
      const users = await getUsers();
      setAuthUsers(users || []);
    } catch (error) {
      console.error('Error fetching auth users:', error);
      setAuthUsers([]);
    }
  };

  const fetchForeignKeyData = async () => {
    const fkData: Record<string, any[]> = {};
    
    for (const field of fields) {
      if (field.foreignKey) {
        try {
          // Get all columns for flexibility with composite labels
          const { data, error } = await supabase
            .from(field.foreignKey.table)
            .select('*')
            .order('id');

          if (!error && data) {
            fkData[field.name] = data;
          }
        } catch (err) {
          console.error(`Error fetching foreign key data for ${field.name}:`, err);
        }
      }
      // For 'targets' fields, load each reference table separately, keyed by
      // `${field.name}::${target.type}`.
      if (field.type === 'targets' && field.targets) {
        for (const target of field.targets) {
          try {
            const { data, error } = await supabase.from(target.table).select('*').order('id');
            if (!error && data) fkData[`${field.name}::${target.type}`] = data;
          } catch (err) {
            console.error(`Error fetching targets data for ${field.name}/${target.type}:`, err);
          }
        }
      }
    }

    setForeignKeyData(fkData);
  };

  const fetchRecord = async () => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('id', recordId)
        .single();

      if (error) throw error;
      
      // Special handling for companies table - map classifications column to classification_id field
      if (tableName === 'companies' && data) {
        const classificationData = data.classifications || data.classification_id;
        if (classificationData) {
          // Parse if it's a JSON string
          let classIds;
          if (typeof classificationData === 'string') {
            try {
              classIds = JSON.parse(classificationData);
            } catch (e) {
              classIds = [classificationData];
            }
          } else if (Array.isArray(classificationData)) {
            classIds = classificationData;
          } else {
            classIds = [classificationData];
          }
          data.classification_id = classIds.filter((id: any) => id != null && id !== '');
        }
      }
      
      // Merge any seeded values (agent-proposed edits) over the loaded record.
      setFormData({ ...(data || {}), ...(initialData || {}) });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleChange = (field: string, value: any) => {
    const newData = { ...formData, [field]: value };
    
    // If onFieldChange callback is provided, use it to compute additional changes
    if (onFieldChange) {
      const updatedData = onFieldChange(field, value, newData);
      if (updatedData) {
        setFormData(updatedData);
        return;
      }
    }
    
    setFormData(newData);
  };

  const handleFileUpload = async (
    fieldName: string,
    bucket: string,
    file: File,
  ) => {
    setError('');
    setUploading((u) => ({ ...u, [fieldName]: true }));
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${tableName}/${Date.now()}_${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(path, file, { upsert: true, cacheControl: '3600' });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      handleChange(fieldName, data.publicUrl);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [fieldName]: false }));
    }
  };

  const handleCreateAuthUser = () => {
    setShowAuthUserConfirm(true);
    setAuthUserCreated(null);
  };

  const handleConfirmCreateAuthUser = async () => {
    if (!formData.id || !formData.email) {
      setError(t('companies.noEmailError') || 'Company must have an email address to create auth user');
      setShowAuthUserConfirm(false);
      return;
    }
    
    setCreatingAuthUser(true);
    setError('');
    try {
      const result = await createAuthUserForCompany(formData.id);
      setAuthUserCreated({
        email: formData.email,
        password: result.tempPassword
      });
      // Refresh auth users list
      await fetchAuthUsers();
      setShowAuthUserConfirm(false);
    } catch (error: any) {
      console.error('Error creating auth user:', error);
      setError(error.message || 'Failed to create auth user');
      setShowAuthUserConfirm(false);
    } finally {
      setCreatingAuthUser(false);
    }
  };

  const handleCancelAuthUserCreation = () => {
    setShowAuthUserConfirm(false);
  };

  const handleCloseAuthUserSuccess = () => {
    setAuthUserCreated(null);
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      // Prepare the data for submission
      const submitData = { ...formData };
      
      // Special handling for companies table - map classification_id field back to classifications column
      if (tableName === 'companies' && submitData.classification_id) {
        submitData.classifications = submitData.classification_id;
        delete submitData.classification_id;
      }
      
      // Never send read-only/system columns.
      delete submitData.created_at;
      delete submitData.updated_at;

      // Normalize every date field to YYYY-MM-DD (or null) before saving.
      // formData can hold whatever a source handed us verbatim — a raw DB
      // value, or a non-ISO string the AI agent proposed (e.g. "26/07/2026",
      // an Arabic month name, a full timestamp) — and until now only the
      // on-screen <input> was normalized (via toInputDateFormat at render
      // time); the actual submitted value was never re-synced, so an
      // untouched date field could still submit a malformed string straight
      // to Postgres (silent save failure, or worse, a misparsed date).
      for (const field of fields) {
        if (field.type === 'date' && field.name in submitData) {
          submitData[field.name] = toInputDateFormat(submitData[field.name]) || null;
        }
      }

      if (changeSource === 'agent') {
        // Route through the audited admin-only RPC (records source='agent').
        if (isEdit) delete submitData.id;
        const row = await applyAgentChange(
          tableName,
          isEdit ? 'update' : 'insert',
          isEdit ? String(recordId) : null,
          submitData,
        );
        onSaved?.(row);
      } else if (isEdit) {
        const { error } = await supabase
          .from(tableName)
          .update(submitData)
          .eq('id', recordId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from(tableName)
          .insert([submitData]);

        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
          mt: 2
        }}>
          {fields.map((field) => {
            // Determine grid column span based on field width
            const getGridColumn = () => {
              if (field.width === 'full') return { xs: 'span 1', sm: 'span 2', md: 'span 3' };
              if (field.width === 'half') return { xs: 'span 1', sm: 'span 1', md: 'span 2' };
              // Default 'third' - one column on medium+, full on small
              return { xs: 'span 1', sm: 'span 1', md: 'span 1' };
            };

            const gridColumn = getGridColumn();

            // Boolean/Checkbox field
            if (field.type === 'boolean') {
              return (
                <FormControlLabel
                  key={field.name}
                  sx={{ gridColumn }}
                  control={
                    <Checkbox
                      checked={formData[field.name] || false}
                      onChange={(e) => handleChange(field.name, e.target.checked)}
                    />
                  }
                  label={field.label}
                />
              );
            }

            // Currency dropdown
            if (field.type === 'currency') {
              return (
                <FormControl key={field.name} required={field.required} disabled={field.disabled} sx={{ gridColumn }}>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={formData[field.name] || 'USD'}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    label={field.label}
                    disabled={field.disabled}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="NIS">NIS</MenuItem>
                  </Select>
                </FormControl>
              );
            }

            // Select dropdown with static options
            if (field.type === 'select' && field.options) {
              return (
                <FormControl key={field.name} required={field.required} disabled={field.disabled} sx={{ gridColumn }}>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    label={field.label}
                    disabled={field.disabled}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }

            // Targets: multiple reference tables -> tagged objects [{type,id}].
            if (field.type === 'targets' && field.targets) {
              const current: Array<{ type: string; id: any }> = Array.isArray(formData[field.name])
                ? formData[field.name]
                : [];

              const buildLbl = (item: any, labelFields: string) =>
                labelFields.split(',').map((f) => item[f.trim()]).filter(Boolean).join(' - ');

              return (
                <Box key={field.name} sx={{ gridColumn, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {field.targets.map((target) => {
                    const data = foreignKeyData[`${field.name}::${target.type}`] || [];
                    const options = data.map((item) => ({
                      value: item[target.valueField],
                      label: buildLbl(item, target.labelField),
                    }));
                    const selectedIds = current.filter((c) => c.type === target.type).map((c) => c.id);
                    const selectedOptions = options.filter((o) => selectedIds.includes(o.value));

                    return (
                      <Autocomplete
                        key={target.type}
                        multiple
                        disableCloseOnSelect
                        options={options}
                        value={selectedOptions}
                        loading={data.length === 0}
                        onChange={(_, newValue) => {
                          // Replace this type's selections, keep other types intact.
                          const others = current.filter((c) => c.type !== target.type);
                          const updated = [
                            ...others,
                            ...newValue.map((v) => ({ type: target.type, id: v.value })),
                          ];
                          handleChange(field.name, updated);
                        }}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} style={{ gap: 8 }}>
                            <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} checked={selected} />
                            {option.label}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label={target.label} placeholder={t('dialog.searchAndSelect')} />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return <Chip key={key} label={option.label} {...tagProps} size="small" />;
                          })
                        }
                      />
                    );
                  })}
                </Box>
              );
            }

            // Multi-select with foreign key (Autocomplete)
            if (field.type === 'multiselect' && field.foreignKey) {
              const fkData = foreignKeyData[field.name] || [];
              
              // Helper function to build composite label from comma-separated fields
              const buildLabel = (item: any, labelFields: string) => {
                const fields = labelFields.split(',').map(f => f.trim());
                return fields.map(f => item[f]).filter(Boolean).join(' - ');
              };
              
              // Create options with labels
              const options = fkData.map((item) => ({
                value: item[field.foreignKey!.valueField],
                label: buildLabel(item, field.foreignKey!.labelField),
                rawData: item,
              }));
              
              // Get selected values (ensure it's an array)
              const selectedValues = Array.isArray(formData[field.name]) 
                ? formData[field.name] 
                : (formData[field.name] ? [formData[field.name]] : []);
              
              const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
              
              const icon = <CheckBoxOutlineBlank fontSize="small" />;
              const checkedIcon = <CheckBox fontSize="small" />;
              
              return (
                <Box key={field.name} sx={{ gridColumn }}>
                  <Autocomplete
                    multiple
                    options={options}
                    disableCloseOnSelect
                    value={selectedOptions}
                    onChange={(_, newValue) => {
                      handleChange(field.name, newValue.map((v) => v.value));
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disabled={field.disabled}
                    loading={fkData.length === 0}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ gap: 8 }}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        required={field.required && selectedOptions.length === 0}
                        placeholder={t('dialog.searchAndSelect')}
                      />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.label}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                </Box>
              );
            }

            // Foreign key dropdown (searchable, supports composite labels via
            // comma-separated labelField e.g. "name,pcu_id").
            if (field.foreignKey && foreignKeyData[field.name]) {
              const buildFkLabel = (item: any) =>
                field.foreignKey!.labelField
                  .split(',')
                  .map((f) => item[f.trim()])
                  .filter(Boolean)
                  .join(' — ');

              const fkOptions = foreignKeyData[field.name].map((item) => ({
                value: item[field.foreignKey!.valueField],
                label: buildFkLabel(item),
              }));
              const selectedFk = fkOptions.find((o) => o.value === formData[field.name]) || null;

              return (
                <Box key={field.name} sx={{ gridColumn }}>
                  <Autocomplete
                    options={fkOptions}
                    value={selectedFk}
                    onChange={(_, newValue) => handleChange(field.name, newValue ? newValue.value : '')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disabled={field.disabled}
                    loading={foreignKeyData[field.name].length === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        required={field.required}
                        placeholder={t('dialog.searchAndSelect')}
                      />
                    )}
                  />
                </Box>
              );
            }

            // Image / file upload -> uploads to a storage bucket, stores public URL
            if (field.type === 'image' || field.type === 'file') {
              const value = formData[field.name];
              const isImage = field.type === 'image';
              const bucket = field.bucket || 'documents';
              const busy = !!uploading[field.name];
              return (
                <Box key={field.name} sx={{ gridColumn, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{field.label}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {value && isImage && (
                      <Box
                        onClick={() => setPreviewImage(value)}
                        sx={{
                          position: 'relative', width: 64, height: 64, borderRadius: 1, overflow: 'hidden',
                          border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                          '&:hover .zoom-overlay': { opacity: 1 },
                        }}
                      >
                        <Box component="img" src={value} alt={field.label}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <Box className="zoom-overlay" sx={{
                          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.4)', opacity: 0, transition: 'opacity 0.15s',
                        }}>
                          <ZoomIn sx={{ color: 'white', fontSize: 22 }} />
                        </Box>
                      </Box>
                    )}
                    {value && !isImage && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 260 }}>
                        <InsertDriveFile fontSize="small" color="action" />
                        <Box component="a" href={value} target="_blank" rel="noopener"
                          sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {String(value).split('/').pop()}
                        </Box>
                      </Box>
                    )}
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={busy ? <CircularProgress size={14} /> : <CloudUpload sx={{ fontSize: 16 }} />}
                      disabled={busy || field.disabled}
                      sx={{ gap: 0.5 }}
                    >
                      {value ? t('dialog.replace') : t('dialog.upload')}
                      <input
                        type="file"
                        hidden
                        accept={field.accept || (isImage ? 'image/*' : undefined)}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(field.name, bucket, file);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {value && (
                      <Button size="small" color="error" onClick={() => handleChange(field.name, null)} sx={{ minWidth: 0, gap: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            }

            // Rich text (HTML) field for large content — news body, board
            // decisions, letters, etc. Sanitized on every change.
            if (field.type === 'richtext') {
              return (
                <Box key={field.name} sx={{ gridColumn }}>
                  <RichTextEditor
                    label={field.label}
                    value={formData[field.name] || ''}
                    onChange={(html) => handleChange(field.name, html)}
                    disabled={field.disabled}
                  />
                </Box>
              );
            }

            // Date fields need special handling for InputLabelProps
            if (field.type === 'date') {
              return (
                <TextField
                  key={field.name}
                  label={field.label}
                  type="date"
                  size="small"
                  value={toInputDateFormat(formData[field.name])}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  disabled={field.disabled}
                  InputLabelProps={{ shrink: true }}
                  sx={{ gridColumn }}
                />
              );
            }

            // Regular text fields
            return (
              <TextField
                key={field.name}
                label={field.label}
                type={field.type || 'text'}
                size="small"
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
                disabled={field.disabled}
                multiline={field.multiline || field.type === 'textarea'}
                rows={field.multiline || field.type === 'textarea' ? 4 : 1}
                sx={{ gridColumn }}
              />
            );
          })}
        </Box>

        {/* Per-record change history (only when editing an existing record) */}
        {isEdit && recordId && (
          <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('activity.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RecordHistory table={tableName} recordId={recordId} />
            </AccordionDetails>
          </Accordion>
        )}
    </>
  );

  const actionsContent = (
    <>
      <Button onClick={onClose} disabled={loading}>{t('dialog.cancel')}</Button>

      {/* Show "Create Auth User" button for companies that have an email and are being edited */}
      {tableName === 'companies' && isEdit && formData.email && !authUsers.some(u => u.user_metadata?.company_id === formData.id) && !authUserCreated && (
        <Button
          onClick={handleCreateAuthUser}
          sx={{ gap: 1 }}
          color="secondary"
          disabled={loading || creatingAuthUser}
        >
          <PersonAdd sx={{ fontSize: 18 }} />{t('companies.createAuthUser') || 'Create Auth User'}
        </Button>
      )}

      <Button onClick={handleSubmit} variant="contained" disabled={loading}>
        {loading ? t('dialog.saving') : t('dialog.save')}
      </Button>
    </>
  );

  // Auth-user sub-dialogs stay real modals regardless of variant.
  const subDialogs = (
    <>
      {/* Full-size image preview — click any thumbnail to view it, not just replace it. */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md">
        <Box
          component="img"
          src={previewImage || ''}
          alt=""
          sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', width: 'auto', height: 'auto' }}
        />
      </Dialog>

      <Dialog open={showAuthUserConfirm} onClose={handleCancelAuthUserCreation} maxWidth="sm">
        <DialogTitle>
          {t('companies.createAuthUser') || 'Create Auth User'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('companies.createAuthUserConfirm') || 'Are you sure you want to create an authentication user for this company?'}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>{t('companies.companyName') || 'Company'}:</strong> {formData.name}
          </DialogContentText>
          <DialogContentText>
            <strong>{t('companies.email') || 'Email'}:</strong> {formData.email}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('companies.autoPasswordGeneration') || 'A temporary password will be automatically generated for this user.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAuthUserCreation} disabled={creatingAuthUser}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirmCreateAuthUser}
            variant="contained"
            disabled={creatingAuthUser}
            sx={{ gap: 1 }}
          >
            {creatingAuthUser ? <CircularProgress size={16} color="inherit" /> : <PersonAdd sx={{ fontSize: 18 }} />}
            {creatingAuthUser
              ? (t('common.creating') || 'Creating...')
              : (t('common.create') || 'Create')
            }
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!authUserCreated} onClose={handleCloseAuthUserSuccess} maxWidth="sm">
        <DialogTitle>
          {t('companies.authUserCreatedTitle') || 'Auth User Created Successfully'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('companies.authUserCreatedMessage') || 'Authentication user has been created successfully!'}
          </Alert>
          {authUserCreated && (
            <>
              <DialogContentText>
                <strong>{t('companies.email') || 'Email'}:</strong> {authUserCreated.email}
              </DialogContentText>
              <DialogContentText sx={{ mt: 1 }}>
                <strong>{t('companies.temporaryPassword') || 'Temporary Password'}:</strong> {authUserCreated.password}
              </DialogContentText>
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('companies.passwordWarning') || 'Please save this password securely. It will not be shown again. The user should change this password on first login.'}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAuthUserSuccess} variant="contained">
            {t('common.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (variant === 'page') {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {isEdit ? t('dialog.editRecord') : t('dialog.addRecord')}
        </Typography>
        {formContent}
        <Box
          sx={{
            display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, pt: 2,
            borderTop: 1, borderColor: 'divider',
            position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 1,
          }}
        >
          {actionsContent}
        </Box>
        {subDialogs}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? t('dialog.editRecord') : t('dialog.addRecord')}</DialogTitle>
      <DialogContent>{formContent}</DialogContent>
      <DialogActions>{actionsContent}</DialogActions>
      {subDialogs}
    </Dialog>
  );
};

export default DetailDialog;
